import { forwardRef, createElement, useRef, useEffect, Fragment } from 'react';
import cn from 'classnames';
import { Icon } from '../Icon/Icon.js';
import { Badge } from '../Badge/Badge.js';
import { Root, Trigger, Content } from '@radix-ui/react-popover';
import { getInputClasses } from '../Input/util.js';
import { useBoundingClientRectListener } from '../../utils/hooks/useBoundingClientRectListener.js';
import { Combobox } from '../Combobox/Combobox.js';
import { MultiListbox, Listbox } from '../Listbox/Listbox.js';
import { useSelect } from './useSelect.js';

const BaseSelect = /*#__PURE__*/forwardRef(function BaseSelect(props, ref) {
  const {
    autoFocus,
    className: externalClassName,
    highlighted,
    style,
    ...otherProps
  } = props;
  const {
    button,
    listbox,
    popover,
    input,
    text,
    more = 0
  } = useSelect(otherProps, ref);
  const internalRef = useRef(null);
  const selectDimensions = useBoundingClientRectListener(internalRef);
  const className = cn('inline-flex relative w-full', {
    'yt-select--readonly': props.readOnly
  }, externalClassName);
  const inputClassname = cn(getInputClasses(props), 'h-8 text-left pr-0', {
    'border-blue': popover.open
  });
  useEffect(() => {
    if (autoFocus && internalRef.current) {
      internalRef.current.focus();
    }
  }, []);

  const renderMultiSelection = () => {
    return createElement(Fragment, null, createElement("span", {
      className: "flex-grow truncate text-left"
    }, text), more > 0 && createElement(Badge, {
      className: "ml-2"
    }, `+${more}`));
  };

  const commonListboxProps = { ...listbox,
    className: 'w-auto',
    invalid: undefined,
    style: {
      minWidth: selectDimensions === null || selectDimensions === void 0 ? void 0 : selectDimensions.width
    },
    tabIndex: popover.open ? 0 : -1
  };
  return createElement("span", {
    className: className,
    "data-taco": "select",
    style: style
  }, createElement(Root, Object.assign({}, popover), createElement(Trigger, Object.assign({}, button, {
    className: inputClassname,
    ref: internalRef
  }), props.multiselect ? renderMultiSelection() : createElement("span", {
    className: "flex-grow truncate text-left"
  }, text), createElement("span", {
    className: "flex h-8 w-8 items-center justify-center"
  }, createElement(Icon, {
    className: "pointer-events-none",
    name: popover.open ? 'chevron-up' : 'chevron-down'
  }))), createElement(Content, {
    align: "start",
    sideOffset: 4
  }, props.multiselect ? createElement(MultiListbox, Object.assign({}, commonListboxProps)) : createElement(Listbox, Object.assign({}, commonListboxProps))), createElement("input", Object.assign({}, input, {
    className: "hidden",
    type: "text"
  }))));
});
const Select = /*#__PURE__*/forwardRef(function Select(props, ref) {
  const {
    editable,
    ...otherProps
  } = props;

  if (editable) {
    return createElement(Combobox, Object.assign({}, otherProps, {
      dialog: undefined,
      inline: true,
      ref: ref
    }));
  }

  return createElement(BaseSelect, Object.assign({}, otherProps, {
    ref: ref
  }));
});

export { Select };
//# sourceMappingURL=Select.js.map
